import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { FaHome, FaRulerCombined, FaMapMarkerAlt, FaUser, FaPhone, FaEnvelope, FaCheckCircle } from "react-icons/fa";
import { MdChair, MdClose, MdConstruction, MdVpnKey, MdCheck, MdHomeWork } from "react-icons/md";
import { OwnerLeadPropTypes } from "../../utils/formDto";
import LocalAutocomplete from "../autocomplete/LocalAutocomplete";
import { API_URLS } from "../../config/api";

const OwnerLeadForm = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    propertyType: "Appartement",
    surface: "",
    rooms: "",
    postalCode: "",
    isOwner: "Oui",
    furnished: "Non",
    available: "Immédiate",
    works: "Non",
    civility: "M.",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const update = (changes) => setForm((f) => ({ ...f, ...changes }));

  const updateAddress = (field, value) => {
    if (value && value.code) {
      // Utiliser uniquement le code postal (sans le nom de la ville)
      setForm((f) => ({ ...f, postalCode: value.code }));
    }
  };

  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => (s > 0 ? s - 1 : s));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.isOwner !== "Oui") {
      setMessage({ ok: false, text: "Notre service de gestion locative est réservé aux propriétaires uniquement. Nous prenons en charge votre bien en tant que locataire professionnel." });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      // Récupération des données de géolocalisation
      const { data: geoData } = await axios.get("https://ipgeolocation.abstractapi.com/v1/?api_key=ce0de992545c4e1ca8ce4a5aa93abb49");
      
      // Normaliser le numéro de téléphone (10 chiffres)
      const cleanPhone = form.phone.replace(/\D/g, '').substring(0, 10).padEnd(10, '0');
      
      // Préparer le payload avec tous les champs du formulaire
      const payload = {
        propertyType: form.propertyType,
        surface: form.surface,
        rooms: form.rooms,
        postalCode: form.postalCode,
        isOwner: form.isOwner,
        furnished: form.furnished,
        available: form.available,
        works: form.works,
        civility: form.civility,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: cleanPhone,
        email: form.email,
        source: 'owner-form',
        timestamp: new Date().toISOString(),
        userInfos: JSON.stringify({
          ...geoData,
          formType: 'gestion-locative',
          leadSource: 'website-owner-form',
          submittedAt: new Date().toISOString()
        })
      };
      
      // Validate payload shape against PropTypes DTO
      PropTypes.checkPropTypes(OwnerLeadPropTypes, payload, "prop", "OwnerLeadForm payload");
      
      console.log('📤 Envoi des données OwnerLeadForm:', payload);
      
      // Envoi vers notre API backend (nouveau endpoint dédié)
      const response = await axios.post(API_URLS.ownerLeadSubmit, payload);
      
      console.log('✅ Réponse API:', response.data);

      setSubmitted(true);
      // reset minimal fields (kept for analytics if needed)
      setForm((f) => ({ ...f, phone: "", email: "" }));
      
    } catch (err) {
      console.error('❌ Erreur lors de l\'envoi du formulaire OwnerLeadForm:', err);
      if (err.response) {
        console.error('Détails de l\'erreur:', err.response.data);
      }
      setMessage({ ok: false, text: "Une erreur est survenue lors de l'envoi. Réessayez plus tard." });
    } finally {
      setSubmitting(false);
    }
  };

  // If already submitted, render only the thank-you step
  if (submitted) {
    return (
      <div className="thank-you-step" style={{ textAlign: "center", padding: 20 }}>
        <FaCheckCircle style={{ color: "#28a745", fontSize: 48 }} />
        <h3 style={{ marginTop: 12 }}>Demande reçue avec succès !</h3>
        <p>Merci pour votre intérêt pour notre service de gestion locative professionnelle.</p>
        <p>Un expert vous recontactera sous 48h pour :</p>
        <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: 10 }}>
          <li>Estimer le potentiel de rentabilité de votre bien</li>
          <li>Vous expliquer notre processus de gestion complète</li>
          <li>Répondre à toutes vos questions</li>
        </ul>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
          <a href={process.env.PUBLIC_URL + "/"} className="axil-btn">Retour à l'accueil</a>
          <a href={process.env.PUBLIC_URL + "/contact"} className="axil-btn btn-fill-primary">En savoir plus</a>
        </div>
      </div>
    );
  }

  return (
    <div className="owner-lead-form">
      <form onSubmit={handleSubmit}>
        {step === 0 && (
          <div>
            <h4>Type de bien à confier</h4>
              <div className="input-with-icon">
              <span className="icon"><FaHome /></span>
              <select id="propertyType" value={form.propertyType} onChange={(e) => update({ propertyType: e.target.value })} className="form-control input-iconed">
                <option>Appartement</option>
                <option>Maison</option>
                <option>Studio</option>
                <option>Bureau</option>
                <option>Local commercial</option>
              </select>
            </div>
            <div className="form-row" style={{ marginTop: 8 }}>
              <div className="input-with-icon">
                <span className="icon"><FaRulerCombined /></span>
                <input id="surface" value={form.surface} onChange={(e) => update({ surface: e.target.value })} placeholder="Surface (m²)" className="form-control input-iconed" />
              </div>
              <div className="input-with-icon" style={{ marginTop: 8 }}>
                <span className="icon"><FaHome /></span>
                <input 
                  id="rooms" 
                  value={form.rooms} 
                  onChange={(e) => update({ rooms: e.target.value })} 
                  placeholder="Nb pièces" 
                  className="form-control input-iconed" 
                  required
                />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <button 
                type="submit" 
                className="axil-btn btn-fill-primary"
                onClick={(e) => {
                  e.preventDefault();
                  if (form.rooms && form.rooms.trim() !== '') {
                    next();
                  } else {
                    // Trigger native validation
                    const roomsInput = document.getElementById('rooms');
                    if (roomsInput) {
                      roomsInput.reportValidity();
                    }
                  }
                }}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h4>Localisation</h4>
            <div className="input-with-icon autocomplete-wrapper">
              <span className="icon"><FaMapMarkerAlt /></span>
              <LocalAutocomplete setMyValue={updateAddress} initialValue={form.postalCode} />
            </div>
            <div style={{ marginTop: 10 }}>
              <button type="button" className="axil-btn" onClick={prev} style={{ marginRight: 8 }}>Précédent</button>
              <button 
                type="submit" 
                className="axil-btn btn-fill-primary"
                onClick={(e) => {
                  e.preventDefault();
                  if (form.postalCode && form.postalCode.trim() !== '') {
                    next();
                  } else {
                    // Trigger native validation
                    const postalInput = document.getElementById('postalCodeInput');
                    if (postalInput) {
                      postalInput.reportValidity();
                    }
                  }
                }}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h4>Êtes-vous le propriétaire du bien ?</h4>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
              Notre service de gestion locative professionnelle est réservé aux propriétaires.
            </p>
            
            <div className="choice-row">
              <div className="choice-buttons" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className={`choice-btn ${form.isOwner === 'Oui' ? 'active' : ''}`}
                  onClick={() => update({ isOwner: 'Oui' })}
                >
                  <MdHomeWork />
                  <span>Oui, je suis propriétaire</span>
                </button>
                <button
                  type="button"
                  className={`choice-btn ${form.isOwner === 'Non' ? 'active' : ''}`}
                  onClick={() => update({ isOwner: 'Non' })}
                >
                  <MdClose />
                  <span>Non, je suis locataire</span>
                </button>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <button type="button" className="axil-btn" onClick={prev} style={{ marginRight: 8 }}>Précédent</button>
              <button type="button" className="axil-btn btn-fill-primary" onClick={next}>Suivant</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h4>Caractéristiques du bien</h4>
            
            <div className="choice-row">
              <label className="choice-label">Le bien est-il meublé ?</label>
              <div className="choice-buttons">
                <button
                  type="button"
                  className={`choice-btn ${form.furnished === 'Oui' ? 'active' : ''}`}
                  onClick={() => update({ furnished: 'Oui' })}
                >
                  <MdChair />
                  <span>Oui, meublé</span>
                </button>
                <button
                  type="button"
                  className={`choice-btn ${form.furnished === 'Non' ? 'active' : ''}`}
                  onClick={() => update({ furnished: 'Non' })}
                >
                  <MdClose />
                  <span>Non, vide</span>
                </button>
              </div>
            </div>

            <div className="choice-row">
              <label className="choice-label">Disponibilité du bien</label>
              <div className="choice-buttons-split">
                <button
                  type="button"
                  className={`choice-btn choice-btn-full ${form.available === 'Immédiate' ? 'active' : ''}`}
                  onClick={() => update({ available: 'Immédiate' })}
                >
                  <MdVpnKey />
                  <span>Immédiate</span>
                </button>
                <div className="choice-buttons-half">
                  <button
                    type="button"
                    className={`choice-btn ${form.available === 'Dans 1 mois' ? 'active' : ''}`}
                    onClick={() => update({ available: 'Dans 1 mois' })}
                  >
                    <MdCheck />
                    <span>Dans 1 mois</span>
                  </button>
                  <button
                    type="button"
                    className={`choice-btn ${form.available === 'Dans 3 mois' ? 'active' : ''}`}
                    onClick={() => update({ available: 'Dans 3 mois' })}
                  >
                    <MdCheck />
                    <span>Dans 3 mois</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="choice-row">
              <label className="choice-label">Travaux de remise en état nécessaires ?</label>
              <div className="choice-buttons-split">
                <button
                  type="button"
                  className={`choice-btn choice-btn-full ${form.works === 'Non' ? 'active' : ''}`}
                  onClick={() => update({ works: 'Non' })}
                >
                  <MdCheck />
                  <span>Aucun travaux</span>
                </button>
                <div className="choice-buttons-half">
                  <button
                    type="button"
                    className={`choice-btn ${form.works === 'Légers' ? 'active' : ''}`}
                    onClick={() => update({ works: 'Légers' })}
                  >
                    <MdConstruction />
                    <span>Travaux légers</span>
                  </button>
                  <button
                    type="button"
                    className={`choice-btn ${form.works === 'Importants' ? 'active' : ''}`}
                    onClick={() => update({ works: 'Importants' })}
                  >
                    <MdConstruction />
                    <span>Travaux importants</span>
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <button type="button" className="axil-btn" onClick={prev} style={{ marginRight: 8 }}>Précédent</button>
              <button type="button" className="axil-btn btn-fill-primary" onClick={next}>Suivant</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h4>Vos coordonnées</h4>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
              Nos experts vous recontacteront pour une estimation gratuite du potentiel de rentabilité de votre bien.
            </p>
            <select value={form.civility} onChange={(e) => update({ civility: e.target.value })} className="form-control">
              <option>M.</option>
              <option>Mme</option>
            </select>
            <div className="input-with-icon" style={{ marginTop: 8 }}>
              <span className="icon"><FaUser /></span>
              <input value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} placeholder="Prénom *" className="form-control input-iconed" required />
            </div>
            <div className="input-with-icon" style={{ marginTop: 8 }}>
              <span className="icon"><FaUser /></span>
              <input value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} placeholder="Nom *" className="form-control input-iconed" required />
            </div>
            <div className="input-with-icon" style={{ marginTop: 8 }}>
              <span className="icon"><FaPhone /></span>
              <input value={form.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="Téléphone * (format: 0123456789)" className="form-control input-iconed" required />
            </div>
            <div className="input-with-icon" style={{ marginTop: 8 }}>
              <span className="icon"><FaEnvelope /></span>
              <input value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="Email *" className="form-control input-iconed" type="email" required />
            </div>
            <div style={{ marginTop: 15, fontSize: '0.8em', color: '#666' }}>
              * Champs obligatoires
            </div>
            <div style={{ marginTop: 10 }}>
              <button type="button" className="axil-btn" onClick={prev} style={{ marginRight: 8 }}>Précédent</button>
              <button type="submit" className="axil-btn btn-fill-primary" disabled={submitting}>
                {!submitting ? "Envoyer" : "Envoi en cours..."}
              </button>
            </div>
          </div>
        )}

        {message && (
          <div className={`alert ${message.ok ? "alert-success" : "alert-danger"}`} style={{ marginTop: 12 }} role="alert">
            {message.text}
          </div>
        )}

        {/* End form */}
      </form>
    </div>
  );
};

export default OwnerLeadForm;
